/**
 * Body composition, every source in one list (Muscle Growth / Strength model,
 * 29 September 2026). Bodygram is the preferred remote source; InBody and
 * DEXA are the alternatives. Each scan shows what its source returned and
 * nothing more: a field the source did not return stays blank, never derived
 * (DEV-BG-03).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, InfoHint } from './ui';

interface ScanRequest {
  id: string;
  phase: 'BASELINE' | 'MONTH_3' | 'MONTH_6' | 'OTHER';
  state: 'PENDING' | 'NO_CONSENT' | 'NOT_CONFIGURED' | 'COMPLETE' | 'PARTIAL' | 'FAILED' | 'RETAKE_REQUIRED';
  detail: string | null;
  receivedAt: string;
  retakeTexted: boolean;
}

const REQUEST_STATE: Record<ScanRequest['state'], string> = {
  PENDING: 'Sending to Bodygram',
  NO_CONSENT: 'Not scanned: no consent',
  NOT_CONFIGURED: 'Waiting for Bodygram to be set up',
  COMPLETE: 'Scanned',
  PARTIAL: 'Scanned, some measurements missing',
  FAILED: 'Failed',
  RETAKE_REQUIRED: 'Photos need retaking',
};

interface Scan {
  id: string;
  source: 'BODYGRAM' | 'INBODY' | 'DEXA' | 'OTHER';
  phase: 'BASELINE' | 'MONTH_3' | 'MONTH_6' | 'OTHER';
  status: 'COMPLETE' | 'PARTIAL' | 'FAILED' | 'RETAKE_REQUIRED';
  scanDate: string;
  weightLb: number | null;
  bodyFatPct: number | null;
  skeletalMuscleMassLb: number | null;
  waistIn: number | null;
  hipIn: number | null;
  chestIn: number | null;
  objectiveDataUsable: boolean;
}

const SOURCE: Record<Scan['source'], string> = { BODYGRAM: 'Bodygram', INBODY: 'InBody', DEXA: 'DEXA', OTHER: 'Other' };
const PHASE: Record<Scan['phase'], string> = { BASELINE: 'Baseline', MONTH_3: 'Month 3', MONTH_6: 'Month 6', OTHER: '' };
const STATUS: Record<Scan['status'], string | null> = {
  COMPLETE: null,
  PARTIAL: 'Partial',
  FAILED: 'Failed',
  RETAKE_REQUIRED: 'Retake needed',
};

export function BodyCompositionCard({ patientId }: { patientId: string }) {
  const query = useQuery({
    queryKey: ['body-composition', patientId],
    queryFn: () => api.get<{ scans: Scan[]; requests: ScanRequest[] }>(`/patients/${patientId}/body-composition`),
  });
  const scans = query.data?.scans ?? [];
  // A request that did not become a scan still matters: it tells the clinic
  // the photos were not used, and why.
  const requests = (query.data?.requests ?? []).filter((request) => request.state !== 'COMPLETE');
  if (scans.length === 0 && requests.length === 0) return null;

  return (
    <Card
      title="Body composition"
      action={
        <InfoHint label="About body composition">
          Every scan in one list, whichever device took it. Muscle gain goals are measured on
          skeletal muscle mass. A failed or partial scan is shown but never used as a measurement.
        </InfoHint>
      }
    >
      {requests.length > 0 && (
        <ul className="mb-3 space-y-1 text-xs" data-testid="scan-requests">
          {requests.map((request) => (
            <li key={request.id} className="rounded-sm2 border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              <span className="font-semibold">{PHASE[request.phase] || 'Scan'}: {REQUEST_STATE[request.state]}.</span>{' '}
              {request.detail}
              {request.state === 'RETAKE_REQUIRED' && (request.retakeTexted ? ' A retake link was texted.' : ' Ask the patient to retake the photos.')}
            </li>
          ))}
        </ul>
      )}
      <ul className="divide-y divide-ink/5 text-sm" data-testid="body-composition">
        {scans.map((scan) => {
          const status = STATUS[scan.status];
          const parts = [
            scan.weightLb !== null ? `${scan.weightLb} lb` : null,
            scan.skeletalMuscleMassLb !== null ? `muscle ${scan.skeletalMuscleMassLb} lb` : null,
            scan.bodyFatPct !== null ? `body fat ${scan.bodyFatPct}%` : null,
            scan.waistIn !== null ? `waist ${scan.waistIn} in` : null,
            scan.hipIn !== null ? `hip ${scan.hipIn} in` : null,
            scan.chestIn !== null ? `chest ${scan.chestIn} in` : null,
          ].filter(Boolean);
          return (
            <li key={scan.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-ink">
                  {new Date(`${scan.scanDate}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                  {PHASE[scan.phase] ? `, ${PHASE[scan.phase]}` : ''}
                </span>
                <span className="text-xs text-ink-muted">
                  {SOURCE[scan.source]}
                  {status ? ` · ${status}` : ''}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-ink-muted">{parts.length ? parts.join(', ') : 'No measurements returned'}</p>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
